import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { notification } from "antd";
import {
  WHATS_NEW_CMS_PATH,
  fetchWhatsNewAdminList,
  fetchWhatsNewEntry,
  fetchWhatsNewList,
  formatWhatsNewDate,
  markWhatsNewRead,
  splitWhatsNewBody,
} from "./whatsNewApi";
import styles from "./whatsNew.module.scss";

const WhatsNewPage = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const [entries, setEntries] = useState([]);
  const [entry, setEntry] = useState(null);
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    fetchWhatsNewAdminList().then((result) => {
      setCanEdit(!result.forbidden);
    });
  }, []);

  useEffect(() => {
    const locale = i18n.language;
    if (id) {
      fetchWhatsNewEntry(id, locale).then((next) => {
        if (!next) {
          notification.error({ message: t("whats_new.missing") });
          history.replace("/console/whats-new");
          return;
        }
        setEntry(next);
        if (!next.is_read) markWhatsNewRead(id, locale);
      });
      return;
    }
    fetchWhatsNewList(locale).then((result) => {
      setEntries(result.entries || []);
    });
  }, [id, i18n.language, history, t]);

  if (id && !entry) {
    return <div className={styles.page} />;
  }

  if (id && entry) {
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <p className={styles.kicker}>
            {t(`whats_new.product.${entry.product || "platform"}`)} ·{" "}
            {formatWhatsNewDate(entry.published_at, i18n.language)}
          </p>
          <h1 className={styles.title}>{entry.title}</h1>
          {entry.hero_image_url ? (
            <img className={styles.hero} src={entry.hero_image_url} alt="" />
          ) : null}
          <div className={styles.body}>
            {splitWhatsNewBody(entry.body || entry.summary).map((para) => (
              <p key={para}>{para}</p>
            ))}
          </div>
          {(entry.screenshots || []).map((shot) => (
            <figure className={styles.shot} key={shot.url}>
              <img src={shot.url} alt={shot.caption || entry.title} />
              {shot.caption ? (
                <figcaption className={styles.caption}>{shot.caption}</figcaption>
              ) : null}
            </figure>
          ))}
          <div className={styles.actions}>
            {entry.try_it_path ? (
              <button
                type="button"
                className={styles.primary}
                onClick={() => history.push(entry.try_it_path)}
              >
                {t("whats_new.tryIt")}
              </button>
            ) : null}
            <button
              type="button"
              className={styles.secondary}
              onClick={() => history.push("/console/whats-new")}
            >
              {t("whats_new.seeAll")}
            </button>
            {canEdit ? (
              <button
                type="button"
                className={styles.secondary}
                onClick={() => history.push(WHATS_NEW_CMS_PATH)}
              >
                {t("whats_new.cms")}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <h1 className={styles.title}>{t("whats_new.title")}</h1>
        <p className={styles.itemMeta}>{t("whats_new.subtitle")}</p>
        {entries.length === 0 ? (
          <div className={styles.empty}>{t("whats_new.empty")}</div>
        ) : (
          entries.map((item) => (
            <button
              type="button"
              key={item.id}
              className={styles.item}
              onClick={() => history.push(`/console/whats-new/${item.id}`)}
            >
              {item.hero_image_url ? (
                <img className={styles.thumb} src={item.hero_image_url} alt="" />
              ) : (
                <span className={styles.thumb} />
              )}
              <span>
                <p className={styles.itemTitle}>{item.title}</p>
                <p className={styles.itemMeta}>
                  {formatWhatsNewDate(item.published_at, i18n.language)}
                  {item.summary ? ` · ${item.summary}` : ""}
                </p>
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

export default WhatsNewPage;
